/**
 * SiteSnapshotPanel — iter 31.100 (15 Feb 2026)
 *
 * Six's spec: a button that captures an image of every page and files
 * the bundle to Drive so Two can review the site without screenshot
 * relays.
 *
 * Mechanism:
 *   1. POST /api/site-snapshot/start → mints snapshot_id + dated Drive folder
 *   2. For each route in PAGES:
 *        - Open in a hidden iframe (same-origin, JWT in localStorage
 *          carries the auth)
 *        - Wait for the iframe to settle (3s default + readyState check)
 *        - html2canvas the iframe's documentElement → PNG blob
 *        - POST /api/site-snapshot/upload (multipart) — uploads to Drive
 *   3. POST /api/site-snapshot/complete → marks job done
 *   4. UI shows progress + final Drive folder URL
 *
 * Notes:
 *   - Pages are captured sequentially (one iframe at a time) so the
 *     browser doesn't run out of memory on a long site.
 *   - Each capture has a 12s hard ceiling — if a page won't settle, we
 *     skip it and move on rather than blocking the whole run.
 *   - The iframe is sized to 1440×900 so screenshots have a consistent
 *     desktop viewport regardless of the operator's window.
 */
import React, { useState, useEffect, useRef } from "react";
import { Camera, Loader2, ExternalLink, Calendar, AlertTriangle } from "lucide-react";
import html2canvas from "html2canvas";
import { api } from "../lib/api";

// Canonical route list. Order = capture order = what Two reads top-down.
// `auth` flag tells the iframe whether to wait for the protected-route
// gate to redirect (we just give it more settle time).
const PAGES = [
    { name: "home", path: "/" },
    { name: "shredder", path: "/shredder" },
    { name: "ledger", path: "/ledger" },
    { name: "brief", path: "/brief" },
    { name: "comms", path: "/comms" },
    { name: "spy-finance", path: "/spy-finance" },
    { name: "spy-health", path: "/spy-health" },
    { name: "billing", path: "/billing" },
    { name: "devices", path: "/devices" },
    { name: "phone-sentinel", path: "/phone-sentinel" },
    { name: "linkedin", path: "/linkedin" },
    { name: "contacts", path: "/contacts" },
    { name: "target-sight", path: "/target-sight" },
    { name: "incidents", path: "/incidents" },
    { name: "admin", path: "/admin" },
    { name: "cv", path: "/cv" },
    { name: "patterns", path: "/patterns" },
    { name: "callouts", path: "/callouts" },
    { name: "cost", path: "/cost" },
    { name: "team", path: "/team" },
    { name: "vault", path: "/vault" },
    { name: "dossiers", path: "/dossiers" },
    { name: "radio", path: "/radio" },
    { name: "manual", path: "/manual" },
];

const SETTLE_MS = 3500;
const HARD_CEILING_MS = 12000;

function _settle(iframe) {
    // Wait for either readyState complete + SETTLE_MS, or HARD_CEILING_MS,
    // whichever comes first.
    return new Promise((resolve) => {
        const start = Date.now();
        const tick = () => {
            const doc = iframe.contentDocument;
            const ready = doc && doc.readyState === "complete";
            const elapsed = Date.now() - start;
            if (elapsed > HARD_CEILING_MS) {
                resolve({ settled: ready, elapsed });
                return;
            }
            if (ready && elapsed > SETTLE_MS) {
                resolve({ settled: true, elapsed });
                return;
            }
            setTimeout(tick, 250);
        };
        tick();
    });
}

async function _capture(iframe) {
    // html2canvas of the iframe's documentElement. The iframe is
    // same-origin, so html2canvas has full DOM access.
    const doc = iframe.contentDocument;
    if (!doc || !doc.documentElement) {
        throw new Error("iframe document not accessible");
    }
    const canvas = await html2canvas(doc.documentElement, {
        useCORS: true,
        backgroundColor: "#f4ecd8",
        logging: false,
        width: iframe.clientWidth,
        height: doc.documentElement.scrollHeight,
        windowWidth: iframe.clientWidth,
        windowHeight: doc.documentElement.scrollHeight,
    });
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error("toBlob returned null"))),
            "image/png",
            0.92,
        );
    });
}

export default function SiteSnapshotPanel() {
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(null); // { current, total, page }
    const [result, setResult] = useState(null);
    const [err, setErr] = useState("");
    const iframeRef = useRef(null);
    // Iter 31.101 — weekly cron state. Schedule is server-tracked
    // (users.snapshot_schedule) and surfaced via /site-snapshot/schedule-status.
    // Capture itself still runs browser-side (no headless on server),
    // so when due we either auto-fire (if opted in) or show a loud
    // banner so the operator presses the button on next visit.
    const [schedule, setSchedule] = useState(null);
    const [scheduleSaving, setScheduleSaving] = useState(false);
    const autoFiredRef = useRef(false);

    const refreshSchedule = async () => {
        try {
            const { data } = await api.get("/site-snapshot/schedule-status");
            setSchedule(data);
        } catch (e) {
            // Don't pop a toast — schedule is non-critical. Quietly log.
            console.warn("schedule-status failed:", e?.message);
        }
    };

    useEffect(() => {
        refreshSchedule();
    }, []);

    const fire = async (opts = {}) => {
        const silent = !!opts.silent;
        if (!silent && !window.confirm(
            `Capture ${PAGES.length} pages and upload to Google Drive?\n\n` +
            `This will navigate a hidden iframe through every protected route. ` +
            `Takes about ${Math.round(PAGES.length * 6 / 60)} minutes. Drive must be connected.`
        )) return;

        setBusy(true);
        setErr("");
        setResult(null);
        setProgress({ current: 0, total: PAGES.length, page: "starting…" });

        let started;
        try {
            started = await api.post("/site-snapshot/start");
        } catch (e) {
            setErr(`Could not start: ${e?.response?.data?.detail || e.message}`);
            setBusy(false);
            setProgress(null);
            return;
        }
        const { snapshot_id, folder_url, label } = started.data;

        const iframe = iframeRef.current;
        const succeeded = [];
        const skipped = [];

        for (let i = 0; i < PAGES.length; i++) {
            const { name, path } = PAGES[i];
            setProgress({ current: i + 1, total: PAGES.length, page: name });
            try {
                iframe.src = path;
                await _settle(iframe);
                const blob = await _capture(iframe);
                const fd = new FormData();
                fd.append("snapshot_id", snapshot_id);
                fd.append("page_name", name);
                fd.append("file", blob, `${name}.png`);
                await api.post("/site-snapshot/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                succeeded.push(name);
            } catch (e) {
                // Don't break the whole run on one page failing.
                console.warn(`snapshot ${name} failed:`, e);
                skipped.push({ name, reason: e?.message?.slice(0, 80) || "unknown" });
            }
        }

        try {
            await api.post("/site-snapshot/complete", { snapshot_id });
        } catch (e) {
            // Even if complete fails, the user can still see the folder.
            console.warn("snapshot complete failed:", e);
        }

        setResult({
            snapshot_id,
            label,
            folder_url,
            captured: succeeded.length,
            skipped,
        });
        setProgress(null);
        setBusy(false);
        // Clear the iframe so it stops loading anything.
        iframe.src = "about:blank";
        // Iter 31.101 — refresh schedule so the "due" banner clears.
        refreshSchedule();
    };

    // Iter 31.101 — auto-fire effect. When the operator visits /admin
    // and a snapshot is due AND they've opted into auto-fire, run it
    // once per page-load. autoFiredRef ensures we don't re-trigger on
    // re-renders within the same session.
    useEffect(() => {
        if (!schedule || autoFiredRef.current) return;
        if (schedule.due && schedule.auto_fire && !busy) {
            autoFiredRef.current = true;
            // Small delay so the banner is visible before the iframe
            // starts navigating — operator sees what's happening.
            setTimeout(() => fire({ silent: true }), 800);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [schedule]);

    const toggleSchedule = async (patch) => {
        if (!schedule) return;
        setScheduleSaving(true);
        try {
            await api.post("/site-snapshot/schedule", {
                enabled: schedule.enabled,
                auto_fire: schedule.auto_fire,
                ...patch,
            });
            await refreshSchedule();
        } catch (e) {
            setErr(`Schedule update failed: ${e?.response?.data?.detail || e.message}`);
        } finally {
            setScheduleSaving(false);
        }
    };

    return (
        <div className="paper-card border border-dashed border-ink/30 p-4 mb-4" data-testid="admin-site-snapshot">
            <div className="flex flex-wrap items-baseline justify-between gap-3 mb-2">
                <h3 className="font-heading text-sm uppercase tracking-[0.22em] text-ink inline-flex items-center gap-2">
                    <Camera className="w-3.5 h-3.5" /> Site snapshot to Drive
                </h3>
                <span className="label-typed text-[10px]">{PAGES.length} pages · ~{Math.round(PAGES.length * 6 / 60)} min</span>
            </div>
            <p className="font-body text-xs text-ink-soft mb-3 max-w-3xl">
                Captures an image of every protected page and uploads to a dated folder
                under <code className="text-ink">_Site Snapshots/YYYY-MM-DD-HHMM/</code> in your
                Drive. Two reads <code className="text-ink">GET /api/site-snapshot/latest?target_user=&lt;your-email&gt;</code>{" "}
                to get the folder URL automatically. Requires Drive connected on the Shredder page.
            </p>
            {schedule && (
                <div className="mb-3 border border-ink/20 bg-paper-light p-3" data-testid="snapshot-schedule">
                    <div className="flex items-baseline justify-between gap-2 mb-2">
                        <div className="font-heading uppercase text-[10px] tracking-[0.22em] text-ink inline-flex items-center gap-2">
                            <Calendar className="w-3 h-3" /> Weekly schedule
                        </div>
                        <div className="font-body text-[10px] text-ink-soft">
                            {schedule.last_started_at
                                ? `Last: ${schedule.last_label || schedule.last_started_at} · ${schedule.days_since}d ago`
                                : "No snapshot captured yet"}
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-4 font-body text-xs">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={schedule.enabled}
                                disabled={scheduleSaving}
                                onChange={(e) => toggleSchedule({ enabled: e.target.checked })}
                                data-testid="snapshot-schedule-enabled"
                            />
                            Weekly tracking on
                        </label>
                        <label className={`inline-flex items-center gap-2 ${schedule.enabled ? "cursor-pointer" : "opacity-40 cursor-not-allowed"}`}>
                            <input
                                type="checkbox"
                                checked={schedule.auto_fire}
                                disabled={!schedule.enabled || scheduleSaving}
                                onChange={(e) => toggleSchedule({ auto_fire: e.target.checked })}
                                data-testid="snapshot-schedule-auto"
                            />
                            Auto-fire when due (on next /admin visit)
                        </label>
                    </div>
                </div>
            )}
            {schedule?.due && !busy && !result && (
                <div
                    className="mb-3 border-2 border-stamp-amber bg-stamp-amber/10 p-3 flex items-center gap-3"
                    data-testid="snapshot-due-banner"
                >
                    <AlertTriangle className="w-4 h-4 text-stamp-amber shrink-0" />
                    <div className="font-body text-xs text-ink flex-1">
                        Snapshot is <strong>due</strong>
                        {schedule.days_since != null && ` (${schedule.days_since}d since last)`}.
                        {schedule.auto_fire ? " Auto-firing now…" : " Press the button below to capture."}
                    </div>
                </div>
            )}
            <button
                onClick={fire}
                disabled={busy}
                data-testid="snapshot-fire"
                className="font-heading uppercase text-[10px] tracking-[0.22em] px-4 py-2 border-2 border-stamp-red text-stamp-red hover:bg-stamp-red hover:text-paper disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                {busy ? "Capturing…" : "Capture site snapshot"}
            </button>
            {progress && (
                <div className="mt-3 font-body text-xs text-ink-soft" data-testid="snapshot-progress">
                    <div className="flex justify-between mb-1">
                        <span>Capturing <code className="text-ink">{progress.page}</code></span>
                        <span>{progress.current} / {progress.total}</span>
                    </div>
                    <div className="h-1 bg-ink/10 overflow-hidden">
                        <div
                            className="h-full bg-stamp-red transition-all"
                            style={{ width: `${(progress.current / progress.total) * 100}%` }}
                        />
                    </div>
                </div>
            )}
            {err && <div className="mt-3 text-stamp-red font-body text-xs" data-testid="snapshot-error">Error: {err}</div>}
            {result && (
                <div className="mt-4 border-2 border-stamp-red bg-stamp-red/5 p-3" data-testid="snapshot-result">
                    <div className="label-typed text-[10px] text-stamp-red mb-2">
                        Snapshot {result.label} · {result.captured} pages captured
                        {result.skipped.length > 0 && ` · ${result.skipped.length} skipped`}
                    </div>
                    <a
                        href={result.folder_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="snapshot-folder-link"
                        className="font-heading uppercase text-[10px] tracking-[0.22em] inline-flex items-center gap-2 text-ink hover:text-stamp-red"
                    >
                        <ExternalLink className="w-3 h-3" /> Open Drive folder
                    </a>
                    {result.skipped.length > 0 && (
                        <details className="mt-2">
                            <summary className="font-body text-xs cursor-pointer">Skipped pages ({result.skipped.length})</summary>
                            <ul className="font-body text-[10px] text-ink-soft mt-1 space-y-0.5">
                                {result.skipped.map((s, i) => (
                                    <li key={i}>
                                        <code className="text-ink">{s.name}</code> — {s.reason}
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                </div>
            )}
            {/* Hidden capture iframe — same-origin so html2canvas can read it.
                1440×900 desktop viewport gives Two consistent shots. */}
            <iframe
                ref={iframeRef}
                title="snapshot capture surface"
                data-testid="snapshot-iframe"
                style={{
                    position: "absolute",
                    left: "-9999px",
                    top: 0,
                    width: 1440,
                    height: 900,
                    border: 0,
                    pointerEvents: "none",
                }}
                sandbox="allow-same-origin allow-scripts"
                src="about:blank"
            />
        </div>
    );
}
